// Barks: short lines a character says unprompted as the player passes (decision 0054: barks query the event log). A bark
// is a line (a string id) for a speaker (or any speaker), gated by a condition and, optionally, by a recent deed in the
// log ("a defeat nearby in the last two minutes", "a quest done today"). The director picks the highest-priority bark
// that passes, the least recently said first among equals (deterministic: no random draw), and gates them: a gap per
// speaker, a gap across all speakers, a cooldown per bark, and `once` lines said only once. Suggestion: the bark
// director (audio-ui-content.md): decks, gates and memory; this is its deterministic core for a sim.
import type { Condition, Conditions } from "./conditions";
import type { EventLogProvider, LogEntry, LogQuery } from "./log";

/** One bark. */
export interface BarkDef {
  id: string;
  /** Who may say it: an NPC id, or "*" for anyone. */
  speaker: string;
  /** The line: a string id. */
  line: string;
  when?: Condition | string;
  /** A deed in the log this bark answers: matching entries within the last `within` seconds (and `near` metres of the
   *  speaker when given). */
  deed?: Omit<LogQuery, "since" | "near" | "limit"> & { within: number; near?: number };
  /** Higher first (default 0). */
  priority?: number;
  /** Seconds before this bark may come again (default the director's). */
  cooldown?: number;
  /** Said once, ever. */
  once?: boolean;
}

/** The director's gates. */
export interface BarkGates {
  /** Seconds between two barks of one speaker. */
  speakerGap: number;
  /** Seconds between any two barks. */
  gap: number;
  /** A bark's cooldown when it gives none. */
  cooldown: number;
}

/** A bark chosen. */
export interface BarkPick {
  bark: BarkDef;
  speaker: string;
  /** The deed it answers, if any. */
  deed: LogEntry | null;
}

interface BarkState {
  said: Record<string, number>;
  speakers: Record<string, number>;
  last: number;
  once: string[];
}

/** Picks barks. Its state (when each was said) goes in the save. */
export class BarkDirector {
  private s: BarkState = { said: {}, speakers: {}, last: Number.NEGATIVE_INFINITY, once: [] };

  constructor(
    readonly barks: readonly BarkDef[],
    readonly gates: BarkGates,
  ) {
    const ids = new Set<string>();
    for (const b of barks) {
      if (ids.has(b.id)) throw new Error(`barks: "${b.id}" twice`);
      ids.add(b.id);
    }
  }

  /** The bark `speaker` would say at time `now` (standing at `pos`), or null; doesn't mark it said. */
  peek(speaker: string, now: number, ctx: { conditions: Conditions; log: EventLogProvider; pos?: [number, number, number] }): BarkPick | null {
    if (now - this.s.last < this.gates.gap) return null;
    if (now - (this.s.speakers[speaker] ?? Number.NEGATIVE_INFINITY) < this.gates.speakerGap) return null;
    let best: BarkPick | null = null;
    let bestKey: [number, number] | null = null;
    for (const b of this.barks) {
      if (b.speaker !== "*" && b.speaker !== speaker) continue;
      if (b.once && this.s.once.includes(b.id)) continue;
      const said = this.s.said[b.id] ?? Number.NEGATIVE_INFINITY;
      if (now - said < (b.cooldown ?? this.gates.cooldown)) continue;
      if (b.when !== undefined && !ctx.conditions.test(b.when)) continue;
      let deed: LogEntry | null = null;
      if (b.deed) {
        const { within, near, ...q } = b.deed;
        const found = ctx.log.query({ ...q, since: now - within, limit: 1, ...(near !== undefined && ctx.pos ? { near: { pos: ctx.pos, radius: near } } : {}) });
        if (!found.length) continue;
        deed = found[0]!;
      }
      // priority first, then the one said longest ago
      const key: [number, number] = [b.priority ?? 0, -said];
      if (!bestKey || key[0] > bestKey[0] || (key[0] === bestKey[0] && key[1] > bestKey[1])) {
        best = { bark: b, speaker, deed };
        bestKey = key;
      }
    }
    return best;
  }

  /** Pick and mark said (see {@link peek}). */
  pick(speaker: string, now: number, ctx: { conditions: Conditions; log: EventLogProvider; pos?: [number, number, number] }): BarkPick | null {
    const p = this.peek(speaker, now, ctx);
    if (p) this.mark(p.bark.id, speaker, now);
    return p;
  }

  /** Mark a bark said by `speaker` at `now`. */
  mark(id: string, speaker: string, now: number): void {
    this.s.said[id] = now;
    this.s.speakers[speaker] = now;
    this.s.last = now;
    const b = this.barks.find((x) => x.id === id);
    if (b?.once && !this.s.once.includes(id)) this.s.once.push(id);
  }

  snapshot(): unknown {
    // -Infinity is not JSON: a never-said director's `last` saves as null
    return { ...structuredClone(this.s), last: Number.isFinite(this.s.last) ? this.s.last : null };
  }

  restore(data: unknown): void {
    const d = data as Omit<BarkState, "last"> & { last: number | null };
    if (!d || typeof d.said !== "object") throw new Error("barks: not a snapshot");
    this.s = { said: { ...d.said }, speakers: { ...d.speakers }, last: d.last ?? Number.NEGATIVE_INFINITY, once: [...d.once] };
  }
}
